import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const CarouselBuilder = ({ prompt, data }) => {
  const { FiChevronLeft, FiChevronRight, FiDownload, FiInstagram, FiExternalLink, FiShoppingBag, FiImage } = FiIcons;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeTab, setActiveTab] = useState('carousel');

  const slides = [
    {
      title: "The Journey Begins",
      content: prompt || "Generate a prompt first to see your travel story",
      background: "bg-gradient-to-br from-purple-900 to-pink-800"
    },
    {
      title: "Destination Spotlight",
      content: `Experience the magic of ${data?.location || 'your destination'} through the eyes of ${data?.gender?.toLowerCase() || 'a'} melanated travelers.`,
      background: "bg-gradient-to-br from-blue-900 to-teal-800"
    },
    {
      title: "Accessibility Matters",
      content: `Every journey should be inclusive. ${data?.mobility === 'None' ? 'Celebrating all forms of travel' : `Featuring ${data?.mobility?.toLowerCase()} accessibility`}.`,
      background: "bg-gradient-to-br from-green-900 to-emerald-800"
    },
    {
      title: "Cultural Celebration",
      content: `Embracing ${data?.skinTone?.toLowerCase() || 'beautiful'} skin tones and ${data?.hairType?.toLowerCase() || 'natural'} hair textures in travel storytelling.`,
      background: "bg-gradient-to-br from-orange-900 to-red-800"
    },
    {
      title: "Experience the Vibe",
      content: `${data?.vibe || 'Luxe Travel'} energy meets ${data?.experience || 'unforgettable moments'} in this stunning ${data?.location || 'destination'}.`,
      background: "bg-gradient-to-br from-yellow-900 to-orange-800"
    },
    {
      title: "Book Your Adventure",
      content: "Ready to create your own melanated travel story? Your inclusive adventure awaits.",
      background: "bg-gradient-to-br from-indigo-900 to-purple-800"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const exportCarousel = () => {
    // In a real app, this would generate downloadable carousel images
    alert('Carousel export feature - would generate 6 Instagram-ready slides');
  };

  const openCanva = () => {
    // Direct link to Canva
    window.open('https://www.canva.com', '_blank');
  };

  const openCanvaWithTemplate = () => {
    // Direct link to travel-themed templates
    window.open('https://www.canva.com/templates/search/instagram%20carousel%20travel/', '_blank');
  };

  const openPrintify = () => {
    // Open Printify with affiliate link
    window.open('https://try.printify.com/9v8eht6mfcjc', '_blank');
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-gray-800 rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold text-yellow-400">Instagram Carousel Builder</h2>
          <div className="flex items-center space-x-4">
            <span className="text-gray-300">Slide {currentSlide + 1} of {slides.length}</span>
            <button
              onClick={exportCarousel}
              className="bg-pink-500 hover:bg-pink-400 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
            >
              <SafeIcon icon={FiDownload} />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700 mb-6">
          <button
            onClick={() => setActiveTab('carousel')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'carousel'
                ? 'text-yellow-400 border-b-2 border-yellow-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            Carousel Preview
          </button>
          <button
            onClick={() => setActiveTab('design')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'design'
                ? 'text-yellow-400 border-b-2 border-yellow-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            Design Tools
          </button>
          <button
            onClick={() => setActiveTab('merch')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'merch'
                ? 'text-yellow-400 border-b-2 border-yellow-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            POD Merch
          </button>
        </div>

        {activeTab === 'carousel' && (
          <>
            <div className="relative">
              <div className="aspect-square max-w-md mx-auto rounded-lg overflow-hidden shadow-2xl">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.3 }}
                    className={`w-full h-full ${slides[currentSlide].background} p-8 flex flex-col justify-center items-center text-center`}
                  >
                    <h3 className="text-2xl font-bold text-white mb-6">{slides[currentSlide].title}</h3>
                    <p className="text-gray-100 text-lg leading-relaxed">{slides[currentSlide].content}</p>
                    <div className="mt-6 flex items-center space-x-2 text-white">
                      <SafeIcon icon={FiInstagram} className="text-2xl" />
                      <span className="text-sm">@melanatedtravel</span>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full"
              >
                <SafeIcon icon={FiChevronLeft} className="text-xl" />
              </button>

              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full"
              >
                <SafeIcon icon={FiChevronRight} className="text-xl" />
              </button>
            </div>

            <div className="flex justify-center mt-6 space-x-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === currentSlide ? 'bg-yellow-400' : 'bg-gray-600'
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {activeTab === 'design' && (
          <div className="space-y-6">
            <div className="bg-gray-700 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                <SafeIcon icon={FiImage} className="mr-2 text-blue-400" />
                Design Your Carousel with Canva
              </h3>
              <p className="text-gray-300 mb-4">
                Create professional Instagram carousels using Canva's powerful design tools. Perfect for your melanated travel content.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={openCanva}
                  className="bg-purple-500 hover:bg-purple-400 text-white px-6 py-3 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  <SafeIcon icon={FiExternalLink} />
                  <span>Create New Carousel</span>
                </button>
                <button
                  onClick={openCanvaWithTemplate}
                  className="bg-blue-500 hover:bg-blue-400 text-white px-6 py-3 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  <SafeIcon icon={FiExternalLink} />
                  <span>Browse Travel Templates</span>
                </button>
              </div>
            </div>

            <div className="bg-gray-700 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Design Tips for Your Content</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-300 mb-2">Visual Elements</h4>
                  <ul className="text-gray-400 text-sm space-y-1">
                    <li>• Use high-contrast colors for {data?.skinTone?.toLowerCase() || 'melanated'} skin tones</li>
                    <li>• Include {data?.location || 'destination'}-inspired backgrounds</li>
                    <li>• Add {data?.vibe?.toLowerCase() || 'luxe'} aesthetic elements</li>
                    <li>• Ensure accessibility with readable fonts</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-300 mb-2">Content Strategy</h4>
                  <ul className="text-gray-400 text-sm space-y-1">
                    <li>• Start with a hook slide</li>
                    <li>• Include your travel story</li>
                    <li>• Add accessibility information</li>
                    <li>• End with a strong CTA</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gray-700 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Carousel Content Ideas</h3>
              <div className="space-y-3">
                <div className="bg-gray-800 rounded-lg p-3">
                  <h4 className="font-semibold text-white">Slide 1: Hook</h4>
                  <p className="text-gray-300 text-sm">
                    "POV: You're a {data?.gender?.toLowerCase() || 'melanated'} traveler in {data?.location || 'paradise'}"
                  </p>
                </div>
                <div className="bg-gray-800 rounded-lg p-3">
                  <h4 className="font-semibold text-white">Slide 2-4: Story</h4>
                  <p className="text-gray-300 text-sm">
                    Share your {data?.experience?.toLowerCase() || 'travel experience'} with inclusive details
                  </p>
                </div>
                <div className="bg-gray-800 rounded-lg p-3">
                  <h4 className="font-semibold text-white">Slide 5-6: CTA</h4>
                  <p className="text-gray-300 text-sm">
                    Encourage bookings and engagement with your {data?.vibe?.toLowerCase() || 'travel'} brand
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'merch' && (
          <div className="space-y-6">
            <div className="bg-gray-700 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                <SafeIcon icon={FiShoppingBag} className="mr-2 text-green-400" />
                Turn Your Carousel into Merchandise
              </h3>
              <p className="text-gray-300 mb-4">
                Transform your carousel designs into profitable print-on-demand products. Use your travel content to create branded merchandise.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-700 rounded-lg p-6">
                <h4 className="font-semibold text-white mb-3">Carousel-to-Product Ideas</h4>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Convert slide designs to t-shirt prints</li>
                  <li>• Create quote cards from your carousel text</li>
                  <li>• Design travel journals with your aesthetics</li>
                  <li>• Make sticker packs from slide elements</li>
                  <li>• Print travel mugs with carousel quotes</li>
                </ul>
              </div>
              <div className="bg-gray-700 rounded-lg p-6">
                <h4 className="font-semibold text-white mb-3">Current Content Merch</h4>
                <div className="space-y-2 text-gray-300 text-sm">
                  <p>
                    <strong>Location:</strong> "{data?.location || 'Paradise'} Vibes" collection
                  </p>
                  <p>
                    <strong>Vibe:</strong> {data?.vibe || 'Luxe Travel'} aesthetic products
                  </p>
                  <p>
                    <strong>Quote:</strong> Extract key phrases from your prompt
                  </p>
                  <p>
                    <strong>Colors:</strong> Match your carousel color palette
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-700 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Quick Setup Guide</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                    1
                  </span>
                  <div>
                    <h4 className="font-semibold text-white">Export Your Carousel</h4>
                    <p className="text-gray-300 text-sm">Download your carousel slides as high-res images</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                    2
                  </span>
                  <div>
                    <h4 className="font-semibold text-white">Choose POD Platform</h4>
                    <p className="text-gray-300 text-sm">Upload designs to Printful, Printify, or similar service</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                    3
                  </span>
                  <div>
                    <h4 className="font-semibold text-white">Create Products</h4>
                    <p className="text-gray-300 text-sm">Apply your designs to t-shirts, mugs, stickers, etc.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                    4
                  </span>
                  <div>
                    <h4 className="font-semibold text-white">Market & Sell</h4>
                    <p className="text-gray-300 text-sm">
                      Promote your merch in your travel content and social media
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => window.open('https://www.printful.com', '_blank')}
                className="bg-blue-500 hover:bg-blue-400 text-white px-4 py-3 rounded-lg flex items-center space-x-2"
              >
                <SafeIcon icon={FiExternalLink} />
                <span>Printful</span>
              </button>
              <button
                onClick={openPrintify}
                className="bg-green-500 hover:bg-green-400 text-white px-4 py-3 rounded-lg flex items-center space-x-2"
              >
                <SafeIcon icon={FiExternalLink} />
                <span>Printify</span>
              </button>
              <button
                onClick={openCanva}
                className="bg-purple-500 hover:bg-purple-400 text-white px-4 py-3 rounded-lg flex items-center space-x-2"
              >
                <SafeIcon icon={FiExternalLink} />
                <span>Design in Canva</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CarouselBuilder;